import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, LogOut, Calculator, UtensilsCrossed,
  CreditCard, HelpCircle, Info, Tag, ChevronDown, User, Shield,
  Sun, Moon, BookOpen,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_cf802d02-7a21-497a-89b7-2bcd31caeb90/artifacts/1chviqu2_Screenshot%202026-06-21%20at%2019.30.33-Photoroom.png';

const navLinks = [
  { href: '/',           label: 'Начало',     icon: Calculator },
  { href: '/calculator', label: 'Калкулатор', icon: Calculator },
  { href: '/my-plan',    label: 'Моят план',  icon: UtensilsCrossed },
  { href: '/pricing',    label: 'Цени',       icon: Tag },
  { href: '/faq',        label: 'Въпроси',    icon: HelpCircle },
  { href: '/about',      label: 'За нас',     icon: Info },
];

const getInitials = (user) => {
  if (user?.full_name)
    return user.full_name.trim().split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  return user?.email?.[0]?.toUpperCase() ?? '?';
};

/* ── Light/Dark frosted-glass island (right nav pill only) ── */
const islandStyle = (scrolled, isDark) => ({
  background: isDark
    ? (scrolled ? 'rgba(20,30,50,0.96)' : 'rgba(20,30,50,0.80)')
    : (scrolled ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.80)'),
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)',
  boxShadow: scrolled
    ? isDark ? '0 4px 28px rgba(0,0,0,0.5)' : '0 4px 28px rgba(0,0,0,0.09)'
    : isDark ? '0 2px 14px rgba(0,0,0,0.3)' : '0 2px 14px rgba(0,0,0,0.06)',
  transition: 'all 0.3s ease',
});

export const Header = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <>
      {/* ─── Floating Header ─── */}
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 transition-all duration-300"
        style={{
          background: scrolled
            ? isDark ? 'rgba(12,19,32,0.97)' : 'rgba(247,250,252,0.97)'
            : isDark ? 'rgba(12,19,32,0.88)' : 'rgba(247,250,252,0.88)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: scrolled
            ? isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)'
            : isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)',
          boxShadow: scrolled
            ? isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.07)'
            : 'none',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[78px] flex items-center justify-between gap-6">

          {/* ── Logo ── */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex-shrink-0 flex items-center transition-opacity duration-200 hover:opacity-75"
          >
            <img
              src={LOGO_URL}
              alt="Portion Budget Meals"
              className="h-[44px] w-auto object-contain dark:brightness-0 dark:invert"
            />
          </Link>

          {/* ── Desktop Nav + Actions ── */}
          <div className="flex items-center gap-2">
            {/* Nav links */}
            <nav className="hidden md:flex items-center gap-1" data-testid="header-nav">
              {navLinks.map((link) => {
                const active = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`header-nav-${link.href.replace('/', '')}`}
                    className="relative px-4 py-2 rounded-xl text-[15px] font-medium transition-all duration-200 group"
                    style={{
                      color: active
                        ? isDark ? '#f1f5f9' : '#111827'
                        : isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.52)',
                      background: active
                        ? isDark ? 'rgba(255,255,255,0.11)' : 'rgba(0,0,0,0.08)'
                        : isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.035)',
                      border: active
                        ? isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.13)'
                        : isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                    }}
                  >
                    {/* Hover overlay */}
                    <span
                      className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      style={{ background: isDark ? 'rgba(255,255,255,0.055)' : 'rgba(0,0,0,0.04)' }}
                    />
                    <span className="relative">{link.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Divider */}
            <span
              className="hidden md:block w-px h-6 mx-1 flex-shrink-0"
              style={{ background: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.09)' }}
            />

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-button"
              title={isDark ? 'Превключи на светла тема' : 'Превключи на тъмна тема'}
              className="w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200"
              style={{
                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                border: isDark ? '1px solid rgba(255,255,255,0.09)' : '1px solid rgba(0,0,0,0.08)',
              }}
            >
              {isDark
                ? <Sun className="w-4 h-4 text-amber-400" />
                : <Moon className="w-4 h-4 text-gray-500" />
              }
            </button>

            {/* User / Guest */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="header-profile-trigger"
                    className="flex items-center gap-2 pl-2.5 pr-3.5 py-2 rounded-xl transition-all duration-200 outline-none"
                    style={{
                      background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                      border: isDark ? '1px solid rgba(255,255,255,0.09)' : '1px solid rgba(0,0,0,0.08)',
                    }}
                  >
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-sm flex-shrink-0">
                      <span className="text-[10px] font-bold text-white leading-none">{getInitials(user)}</span>
                    </div>
                    <span className="hidden md:block text-[14px] font-medium text-gray-700 dark:text-gray-300 max-w-[80px] truncate">
                      {user.full_name?.split(' ')[0] || user.email.split('@')[0]}
                    </span>
                    <ChevronDown className="hidden md:block w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="end"
                    sideOffset={12}
                    className="w-56 rounded-2xl p-0 overflow-hidden border-0"
                    style={{
                      background: isDark ? 'rgba(18,28,46,0.98)' : 'rgba(255,255,255,0.98)',
                      backdropFilter: 'blur(20px)',
                      border: isDark ? '1px solid rgba(255,255,255,0.09)' : '1px solid rgba(0,0,0,0.08)',
                      boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 16px 48px rgba(0,0,0,0.12)',
                    }}
                  >
                    {/* User header */}
                    <div className="px-4 pt-4 pb-3" style={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-white">{getInitials(user)}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[12.5px] font-semibold text-gray-900 dark:text-gray-100 truncate">{user.full_name || 'Потребител'}</p>
                          <p className="text-[10.5px] text-gray-400 dark:text-gray-500 truncate">{user.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-1.5">
                      <DropdownMenuItem asChild className="rounded-xl cursor-pointer focus:bg-gray-50 hover:bg-gray-50 dark:focus:bg-white/5 dark:hover:bg-white/5">
                        <Link to="/profile" data-testid="header-profile-link"
                          className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">
                          <User className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                          Профил
                        </Link>
                      </DropdownMenuItem>

                      <DropdownMenuItem asChild className="rounded-xl cursor-pointer focus:bg-gray-50 hover:bg-gray-50 dark:focus:bg-white/5 dark:hover:bg-white/5">
                        <Link to="/wallet" data-testid="header-wallet-link"
                          className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">
                          <CreditCard className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                          Абонамент
                        </Link>
                      </DropdownMenuItem>

                      <DropdownMenuItem asChild className="rounded-xl cursor-pointer focus:bg-gray-50 hover:bg-gray-50 dark:focus:bg-white/5 dark:hover:bg-white/5">
                        <Link to="/my-plan"
                          className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">
                          <UtensilsCrossed className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                          Моят план
                        </Link>
                      </DropdownMenuItem>

                      <DropdownMenuItem asChild className="rounded-xl cursor-pointer focus:bg-gray-50 hover:bg-gray-50 dark:focus:bg-white/5 dark:hover:bg-white/5">
                        <Link to="/cookbook" data-testid="header-cookbook-link"
                          className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">
                          <BookOpen className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                          Готварска книга
                        </Link>
                      </DropdownMenuItem>

                      {user?.is_admin && (
                        <DropdownMenuItem asChild className="rounded-xl cursor-pointer focus:bg-amber-50 hover:bg-amber-50 dark:focus:bg-amber-900/20 dark:hover:bg-amber-900/20">
                          <Link to="/admin" data-testid="header-admin-link"
                            className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-medium text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300">
                            <Shield className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
                            Администрация
                          </Link>
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuSeparator style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', margin: '4px 0' }} />

                      <DropdownMenuItem onClick={handleLogout} data-testid="header-logout-button"
                        className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-medium text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer focus:bg-red-50 dark:focus:bg-red-900/20">
                        <LogOut className="w-3.5 h-3.5 flex-shrink-0" />
                        Изход
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login" data-testid="header-login-link">
                    <button
                      className="hidden md:flex items-center px-4 py-2 text-[14px] font-medium rounded-xl transition-all duration-200"
                      style={{
                        color: isDark ? 'rgba(255,255,255,0.60)' : 'rgba(0,0,0,0.55)',
                        border: isDark ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(0,0,0,0.14)',
                        background: 'transparent',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                        e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.80)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.60)' : 'rgba(0,0,0,0.55)';
                      }}
                    >
                      Вход
                    </button>
                  </Link>
                  <Link to="/calculator">
                    <button
                      data-testid="header-primary-cta-button"
                      className="flex items-center gap-1.5 px-5 py-2 text-[14px] font-semibold text-white rounded-xl transition-all duration-150 hover:opacity-90 active:scale-[0.97]"
                      style={{ background: 'linear-gradient(135deg, #059669, #047857)', boxShadow: '0 0 20px rgba(5,150,105,0.28)' }}
                    >
                      Изчисли сега
                    </button>
                  </Link>
                </div>
              )}

              {/* Mobile hamburger */}
              <button
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl transition-colors ml-1"
                style={{
                  background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                  border: isDark ? '1px solid rgba(255,255,255,0.09)' : '1px solid rgba(0,0,0,0.08)',
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
                }}
                onClick={() => setMobileOpen(true)}
                data-testid="header-mobile-menu-button"
              >
                <Menu className="w-4.5 h-4.5" />
              </button>
          </div>
        </div>
      </header>
      {/* ─── Mobile Full-Screen Overlay ─── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[60] md:hidden flex flex-col"
            style={{ background: 'rgba(11,13,12,0.97)', backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)' }}
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <img src={LOGO_URL} alt="Portion" className="h-9 w-auto object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
              <button
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full"
                style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.55)' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Big navigation links */}
            <nav className="flex-1 px-6 pt-8 flex flex-col gap-1 overflow-y-auto">
              {navLinks.map((link, i) => {
                const active = location.pathname === link.href;
                const Icon = link.icon;
                return (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.055, duration: 0.22, ease: 'easeOut' }}
                  >
                    <Link
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-4 py-1.5 transition-all duration-200"
                      style={{
                        color: active ? '#34d399' : 'rgba(255,255,255,0.30)',
                        transform: active ? 'translateX(8px)' : 'translateX(0)',
                      }}
                    >
                      <Icon className="w-6 h-6 flex-shrink-0" style={{ color: active ? '#34d399' : 'rgba(255,255,255,0.20)' }} />
                      <span style={{ fontSize: '2.2rem', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.03em' }}>
                        {link.label}
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </nav>

            {/* Bottom user strip */}
            <div className="px-6 py-6" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              {user ? (
                <div className="flex flex-col gap-3">
                  {user?.is_admin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      data-testid="mobile-admin-link"
                      className="flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-semibold"
                      style={{ background: 'rgba(217,119,6,0.15)', color: '#fbbf24' }}
                    >
                      <Shield className="w-4 h-4 flex-shrink-0" />
                      Администрация
                    </Link>
                  )}
                  <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-white">{getInitials(user)}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{user.full_name || 'Потребител'}</p>
                      <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.35)' }}>{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { handleLogout(); setMobileOpen(false); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium flex-shrink-0"
                    style={{ color: '#f87171', background: 'rgba(239,68,68,0.10)' }}
                  >
                    <LogOut className="w-3.5 h-3.5" /> Изход
                  </button>
                </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1">
                    <button className="w-full py-3 rounded-full text-sm font-medium transition-colors"
                      style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.55)' }}>
                      Вход
                    </button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1">
                    <button className="w-full py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold text-white transition-colors">
                      Регистрация
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
